import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Shield, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import SectionPdfExport, { buildColumns } from '@/components/SectionPdfExport';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

export default function SolvencyPage({ data, results, analysis }) {
  const solvency = results?.solvency || {};
  const seller = solvency.seller;
  const buyer = solvency.buyer;
  const dcf = results?.dcf || {};
  const dealComparison = results?.deal_comparison || {};

  // Get both DSCR values
  const brokerDSCR = buyer?.dscr || 0;
  const dcfAdjustedDSCR = dcf?.dcf_adjusted_dscr || 0;
  const dcfYear1FCF = dcf?.dcf_year1_fcf || 0;
  const totalDebtService = dcf?.total_debt_service || dealComparison?.buyer?.total_debt_service || 0;

  // Get SDE info for broker DSCR calculation display
  const brokerSDE = data?.seller_deal?.sde || dealComparison?.buyer?.sde || 0;
  const buyerSalary = data?.buyer_deal?.buyer_salary || dealComparison?.buyer?.buyer_salary || 75000;

  // SBA threshold check
  const showSBAWarning = dcfAdjustedDSCR > 0 && dcfAdjustedDSCR < 1.35 && dcfAdjustedDSCR !== Infinity;
  const hasDebtService = totalDebtService > 0;

  if (!seller && !buyer) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-zinc-500 text-sm" data-testid="solvency-page">
        Import data to view solvency analysis
      </div>
    );
  }

  // Prepare rows for PDF export
  const solvencyRows = [
    { metric: 'Current Ratio', seller: seller?.current_ratio, buyer: buyer?.current_ratio },
    { metric: 'Debt-to-Equity', seller: seller?.debt_to_equity, buyer: buyer?.debt_to_equity },
    { metric: 'Solvency Ratio', seller: seller?.solvency_ratio, buyer: buyer?.solvency_ratio },
    { metric: 'DSCR (Broker)', seller: seller?.dscr, buyer: buyer?.dscr },
    { metric: 'DSCR (DCF-Adjusted)', seller: null, buyer: dcfAdjustedDSCR },
  ];

  const ratios = [
    { label: 'Current Ratio', key: 'current_ratio', suffix: 'x', benchmark: '> 1.5x healthy', goodThreshold: 1.5, format: (v) => v?.toFixed(4) },
    { label: 'Debt-to-Equity Ratio', key: 'debt_to_equity', suffix: 'x', benchmark: '< 2.0x preferred', goodThreshold: 2.0, isLower: true, format: (v) => v?.toFixed(4) },
    { label: 'Solvency Ratio', key: 'solvency_ratio', suffix: 'x', benchmark: '> 1.0x = solvent', goodThreshold: 1.0, format: (v) => v?.toFixed(4) },
  ];

  const balanceItems = [
    { label: 'Cash / Working Capital', key: 'current_assets', section: 'assets' },
    { label: 'Total Current Assets', key: 'current_assets', section: 'total' },
    { label: 'Total Current Liabilities', key: 'current_liabilities', section: 'liabilities' },
    { label: 'Total Debt (Long-Term)', key: 'total_debt', section: 'liabilities' },
    { label: 'Total Equity', key: 'total_equity', section: 'equity' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="solvency-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Solvency Examination</h2>
          <p className="text-sm text-zinc-400 mt-1">Current ratio, debt-to-equity, solvency ratio & DSCR analysis</p>
        </div>
        <SectionPdfExport
          title="Solvency Analysis"
          analysisName={analysis?.name}
          rows={solvencyRows}
          columns={buildColumns([
            { label: 'Metric', key: 'metric' },
            { label: 'Seller', key: 'seller', format: 'ratio' },
            { label: 'Buyer', key: 'buyer', format: 'ratio' },
          ])}
        />
      </div>

      {/* DSCR Dual Display - Featured Section */}
      <Card className={`bg-zinc-900/60 ${showSBAWarning ? 'border-amber-500/40' : 'border-zinc-800/60'}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-indigo-400" />
              <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Debt Service Coverage Ratio (DSCR)</CardTitle>
            </div>
            {showSBAWarning && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-400 text-[10px]">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Below SBA 1.35x
              </Badge>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-1">Comparing broker-reported vs. DCF-adjusted debt coverage</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Broker DSCR */}
            <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">DSCR (Broker/SDE-Based)</span>
                {brokerDSCR >= 1.35 ? (
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                )}
              </div>
              <div className={`font-mono text-3xl font-bold ${brokerDSCR >= 1.35 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {hasDebtService ? `${brokerDSCR?.toFixed(2)}x` : 'N/A'}
              </div>
              <div className="mt-2 space-y-1 text-[10px] text-zinc-500">
                <div className="flex justify-between">
                  <span>SDE (Broker)</span>
                  <span className="font-mono text-zinc-400">{fmt(brokerSDE)}</span>
                </div>
                <div className="flex justify-between">
                  <span>- Buyer Salary</span>
                  <span className="font-mono text-zinc-400">{fmt(buyerSalary)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-zinc-700/50">
                  <span>= Adjusted SDE</span>
                  <span className="font-mono text-zinc-300">{fmt(brokerSDE - buyerSalary)}</span>
                </div>
                <div className="flex justify-between">
                  <span>÷ Debt Service</span>
                  <span className="font-mono text-zinc-400">{fmt(totalDebtService)}</span>
                </div>
              </div>
            </div>

            {/* DCF-Adjusted DSCR */}
            <div className={`p-4 rounded-lg border ${showSBAWarning ? 'bg-amber-500/10 border-amber-500/30' : 'bg-indigo-500/10 border-indigo-500/30'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] uppercase tracking-wider ${showSBAWarning ? 'text-amber-400' : 'text-indigo-400'}`}>DSCR (DCF-Adjusted)</span>
                {dcfAdjustedDSCR >= 1.35 ? (
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                )}
              </div>
              <div className={`font-mono text-3xl font-bold ${dcfAdjustedDSCR >= 1.35 ? 'text-indigo-400' : 'text-amber-400'}`}>
                {hasDebtService ? `${dcfAdjustedDSCR?.toFixed(2)}x` : 'N/A'}
              </div>
              <div className="mt-2 space-y-1 text-[10px] text-zinc-500">
                <div className="flex justify-between">
                  <span>Year 1 FCF (DCF)</span>
                  <span className="font-mono text-zinc-300">{fmt(dcfYear1FCF)}</span>
                </div>
                <div className="flex justify-between text-zinc-600 text-[9px]">
                  <span>(After COGS, OpEx, Salary, CapEx, NWC, Tax)</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-zinc-700/50">
                  <span>÷ Debt Service</span>
                  <span className="font-mono text-zinc-400">{fmt(totalDebtService)}</span>
                </div>
              </div>
              <div className="mt-3 pt-2 border-t border-zinc-700/30">
                <span className={`text-[9px] ${showSBAWarning ? 'text-amber-400' : 'text-indigo-400'}`}>
                  More conservative — reflects actual operating costs
                </span>
              </div>
            </div>
          </div>

          {/* SBA Warning */}
          {showSBAWarning && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-300/90">
                <span className="font-medium">SBA Financing Warning:</span> The DCF-Adjusted DSCR of {dcfAdjustedDSCR?.toFixed(2)}x 
                is below the 1.35x minimum typically required for SBA 7(a) loan approval. The deal may require additional equity 
                injection, a lower purchase price, or alternative financing terms to meet SBA requirements.
              </div>
            </div>
          )}

          {/* DSCR Variance Note */}
          {hasDebtService && Math.abs(brokerDSCR - dcfAdjustedDSCR) > 0.5 && (
            <div className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-700/50 flex items-start gap-2">
              <Info className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-zinc-400">
                <span className="font-medium text-zinc-300">DSCR Gap Analysis:</span> The {((brokerDSCR - dcfAdjustedDSCR) / dcfAdjustedDSCR * 100).toFixed(0)}% difference 
                between broker and DCF-adjusted DSCR suggests the broker's SDE may include add-backs that don't translate to actual cash flow. 
                The DCF-adjusted figure is the more realistic measure of debt serviceability.
              </div>
            </div>
          )}

          {/* No Debt Service Note */}
          {!hasDebtService && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-emerald-300/90">
                <span className="font-medium">All-Cash Deal:</span> No debt service detected. DSCR calculations are not applicable for all-cash acquisitions.
              </div>
            </div>
          )}

          {/* SBA Threshold Reference */}
          <div className="text-[10px] text-zinc-500 pt-2 border-t border-zinc-800/50">
            <span className="text-zinc-400 font-medium">SBA 7(a) Threshold:</span> Minimum 1.35x DSCR required. Above 1.5x is considered comfortable.
            The DCF-Adjusted DSCR uses Year 1 Free Cash Flow from the DCF model, which accounts for actual operating costs rather than broker-reported SDE.
          </div>
        </CardContent>
      </Card>

      {/* Other Ratio Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ratios.map((ratio) => {
          const sv = seller?.[ratio.key];
          const bv = buyer?.[ratio.key];
          const buyerGood = ratio.isLower ? bv <= ratio.goodThreshold : bv >= ratio.goodThreshold;
          const sellerGood = ratio.isLower ? sv <= ratio.goodThreshold : sv >= ratio.goodThreshold;

          return (
            <Card key={ratio.key} className={`bg-zinc-900/60 border-zinc-800/60 ${buyerGood ? 'hover:border-emerald-500/30' : 'hover:border-red-500/30'} transition-colors`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-zinc-400">{ratio.label}</span>
                  {buyerGood ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Seller</span>
                    <div className={`font-mono text-lg font-semibold ${sellerGood ? 'text-emerald-400' : 'text-red-400'}`}>
                      {ratio.format(sv)}{ratio.suffix}
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Buyer</span>
                    <div className={`font-mono text-lg font-semibold ${buyerGood ? 'text-emerald-400' : 'text-red-400'}`}>
                      {ratio.format(bv)}{ratio.suffix}
                    </div>
                  </div>
                </div>

                <div className="mt-2 text-[10px] text-zinc-500 font-mono">{ratio.benchmark}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Balance Sheet Estimates */}
      <Card className="bg-zinc-900/60 border-zinc-800/60 overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Balance Sheet Estimates</CardTitle>
            <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-[10px]">Unaudited</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800/60 hover:bg-transparent">
                <TableHead className="text-zinc-400 text-xs">Item</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Seller Estimate</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Buyer Estimate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {balanceItems.map((item) => (
                <TableRow key={item.label} className="border-zinc-800/40 hover:bg-zinc-800/20">
                  <TableCell className="text-xs text-zinc-300">{item.label}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-zinc-200">{fmt(seller?.[item.key])}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-zinc-200">{fmt(buyer?.[item.key])}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Warning Note */}
      <Card className="bg-zinc-900/60 border-amber-500/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-2 text-xs text-zinc-400">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <span className="text-amber-400 font-medium">No audited balance sheet provided.</span> Estimates are derived from deal tabs, P&L, and asset list.
              Request audited financials for verification. Current ratios and debt-to-equity estimates may vary.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
